export const assetKind = (asset) => ({ CAM: "Câmera", SER: "Servidor", SWI: "Switch", NOB: "Nobreak", RAC: "Rack" })[asset.systemType] || asset.assetType || "Equipamento";
export const assetLabel = (asset) => asset.pointName || asset.name;
export const assetLocation = (asset) => asset.building || asset.area || asset.location || "Local não informado";
const normalizedPlace = (value) => String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/^unidade\s+/i, "").trim().toLocaleLowerCase("pt-BR");

// Only persisted switch/UPS relations are physical links. Location branches merely
// organize the drawing and are rendered with dashed lines.
export function buildTopology(assets, unitName) {
  const byId = new Map(assets.map((asset) => [Number(asset.id), asset]));
  const hasPhysicalLink = (asset) => [asset.switchAssetId, asset.upsAssetId].some((id) => byId.has(Number(id)) && Number(id) !== Number(asset.id));
  // An identified point belongs on the map even when its physical switch is
  // unknown. Its dashed location edge must not be presented as a cable link.
  const displayed = assets;
  const unlinked = assets.filter((asset) => !hasPhysicalLink(asset) && !assets.some((other) => Number(other.switchAssetId) === Number(asset.id) || Number(other.upsAssetId) === Number(asset.id)));
  const locationFor = (asset) => {
    const building = assetLocation(asset);
    if (normalizedPlace(building) !== normalizedPlace(unitName)) return building;
    const moreSpecific = [asset.area, asset.location].find((value) => value && normalizedPlace(value) !== normalizedPlace(unitName));
    return moreSpecific || null;
  };
  const locations = [...new Set(displayed.map(locationFor).filter(Boolean))].sort((a, b) => a.localeCompare(b, "pt-BR"));
  const nodes = [{ id: "unit", type: "unit", label: unitName || "Unidade" }];
  const edges = [];
  const keyForLocation = (name) => `location:${locations.indexOf(name)}`;
  locations.forEach((name) => {
    nodes.push({ id: keyForLocation(name), type: "location", label: name });
    edges.push({ source: "unit", target: keyForLocation(name), type: "group" });
  });
  displayed.forEach((asset) => nodes.push({ id: `asset:${asset.id}`, type: "asset", label: assetLabel(asset), asset, locationId: locationFor(asset) ? keyForLocation(locationFor(asset)) : "unit" }));
  const displayedIds = new Set(displayed.map((asset) => Number(asset.id)));
  const parentOf = new Map();
  displayed.forEach((asset) => {
    const id = Number(asset.id);
    const preferred = [asset.switchAssetId, asset.upsAssetId].map(Number).find((target) => displayedIds.has(target) && target !== id);
    let parent = preferred;
    const seen = new Set([id]);
    while (parent && !seen.has(parent)) {
      seen.add(parent);
      const next = byId.get(parent);
      parent = Number(next?.switchAssetId) || Number(next?.upsAssetId) || 0;
    }
    if (parent && seen.has(parent)) parentOf.set(id, null); // Ignore a cyclic relationship.
    else parentOf.set(id, preferred || null);
  });
  displayed.forEach((asset) => {
    const parent = parentOf.get(Number(asset.id));
    edges.push({ source: parent ? `asset:${parent}` : (locationFor(asset) ? keyForLocation(locationFor(asset)) : "unit"), target: `asset:${asset.id}`, type: parent ? "physical" : "group", port: parent === Number(asset.switchAssetId) ? asset.switchPort : "" });
    if (asset.upsAssetId && Number(asset.upsAssetId) !== parent && displayedIds.has(Number(asset.upsAssetId))) {
      edges.push({ source: `asset:${asset.upsAssetId}`, target: `asset:${asset.id}`, type: "power" });
    }
  });
  const children = new Map(nodes.map((node) => [node.id, []]));
  edges.filter((edge) => edge.type !== "power").forEach((edge) => children.get(edge.source)?.push(edge.target));
  for (const siblings of children.values()) siblings.sort((a, b) => {
    const left = nodes.find((node) => node.id === a);
    const right = nodes.find((node) => node.id === b);
    const rank = (node) => ({ location: 0, NOB: 1, RAC: 2, SWI: 3, SER: 4, CAM: 5 })[node.asset?.systemType || node.type] ?? 6;
    return rank(left) - rank(right) || left.label.localeCompare(right.label, "pt-BR");
  });
  const positions = new Map();
  const grouped = new Map([["unit", []], ...locations.map((name) => [keyForLocation(name), []])]);
  displayed.forEach((asset) => grouped.get(locationFor(asset) ? keyForLocation(locationFor(asset)) : "unit").push(asset));
  const clusters = [...grouped].filter(([id, members]) => id !== "unit" && members.length);
  const columns = Math.max(1, Math.ceil(Math.sqrt(clusters.length)));
  const largestCluster = Math.max(...clusters.map(([, members]) => members.length), grouped.get("unit").length, 1);
  const spacing = Math.max(760, Math.ceil(96 * Math.sqrt(largestCluster) + 200));
  const rows = Math.max(1, Math.ceil(clusters.length / columns));
  positions.set("unit", { x: 100, y: ((rows - 1) * spacing) / 2 + 120 });
  clusters.forEach(([id, members], index) => {
    const center = { x: 650 + (index % columns) * spacing, y: 120 + Math.floor(index / columns) * spacing };
    positions.set(id, center);
    members.sort((a, b) => assetLabel(a).localeCompare(assetLabel(b), "pt-BR"));
    members.forEach((asset, offset) => {
      const angle = offset * 2.399963229728653;
      const radius = 48 * Math.sqrt(offset + 1);
      positions.set(`asset:${asset.id}`, { x: center.x + Math.cos(angle) * radius, y: center.y + Math.sin(angle) * radius });
    });
  });
  grouped.get("unit").forEach((asset, offset) => {
    const angle = offset * 2.399963229728653;
    const radius = 48 * Math.sqrt(offset + 1);
    positions.set(`asset:${asset.id}`, { x: 100 + Math.cos(angle) * radius, y: positions.get("unit").y + Math.sin(angle) * radius });
  });
  const laidOut = nodes.map((node) => ({ ...node, ...positions.get(node.id) }));
  return { nodes: laidOut, edges, unlinked, width: Math.max(550, ...laidOut.map((node) => node.x + 155)), height: Math.max(350, ...laidOut.map((node) => node.y + 88)) };
}
